import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconSearch } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { type Judgment, type SearchLogEntry, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import { useSearch } from "../../api/hooks";
import { snippetToHtml } from "../../lib/snippet";
import { DocumentHeader } from "../search/result-group";
import { EvalTools } from "./eval-tools";

/**
 * Gold-set collection by pooling: run a query, grade the candidate documents (0 = not relevant,
 * 1 = partly, 2 = relevant). Grades are per user; the eval CLI exports them.
 */
export function EvalPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const results = useSearch(
    q
      ? {
          q,
          company: [],
          type: [],
          from: null,
          to: null,
          mode: "hybrid",
          depth: "deep",
          page: 1,
          superseded: false,
          sort: "relevance",
        }
      : null,
  );
  const judgments = useQuery({
    queryKey: ["judgments", q],
    enabled: q.length > 0,
    queryFn: () =>
      api<{ items: Judgment[] }>("/api/judgments", { query: { query: q, mine: "true" } }),
  });
  const log = useAdminQuery<{
    items: SearchLogEntry[];
    topQueries30d: { queryNorm: string; count: number; avgDocs: number }[];
  }>("search-log", "/api/admin/search-log?limit=30");
  const grade = useMutation({
    mutationFn: (v: { documentId: number; grade: number }) =>
      api("/api/judgments", {
        method: "POST",
        body: { query: q, documentId: v.documentId, grade: v.grade },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["judgments", q] }),
    onError: (e: Error) => notifications.show({ message: e.message, color: "red" }),
  });
  const gradeOf = (documentId: number) =>
    judgments.data?.items.find((j) => j.documentId === documentId)?.grade;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setQ(text.trim());
  };

  return (
    <Stack gap="md">
      <EvalTools />
      <Text size="sm" c="dimmed">
        {t("admin.eval.hint")}
      </Text>
      <form onSubmit={submit}>
        <Group>
          <TextInput
            style={{ flex: 1 }}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            placeholder={t("search.placeholder")}
            leftSection={<IconSearch size={16} />}
          />
          <Button type="submit" loading={results.isFetching}>
            {t("search.button")}
          </Button>
        </Group>
      </form>
      <Group align="flex-start" gap="md" wrap="wrap">
        <Stack gap="sm" style={{ flex: 2, minWidth: 320 }}>
          {results.data?.groups.map((g, i) => (
            <Card key={g.document.id} withBorder padding="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ flex: 1 }}>
                  <Anchor
                    component={Link}
                    to={`/documents/${g.document.id}?q=${encodeURIComponent(q)}`}
                    target="_blank"
                    fw={600}
                    size="sm"
                  >
                    {i + 1}. {g.document.title}
                  </Anchor>
                  <DocumentHeader doc={g.document} />
                  {g.fragments[0] && (
                    <Text
                      size="xs"
                      lineClamp={2}
                      // snippetToHtml escapes everything except <mark>
                      dangerouslySetInnerHTML={{
                        __html: snippetToHtml(g.fragments[0].snippetHtml),
                      }}
                    />
                  )}
                </Stack>
                <SegmentedControl
                  size="xs"
                  data={[
                    { value: "0", label: t("admin.eval.grade0") },
                    { value: "1", label: t("admin.eval.grade1") },
                    { value: "2", label: t("admin.eval.grade2") },
                  ]}
                  value={gradeOf(g.document.id) !== undefined ? String(gradeOf(g.document.id)) : ""}
                  onChange={(v) => grade.mutate({ documentId: g.document.id, grade: Number(v) })}
                  color={
                    gradeOf(g.document.id) === 2
                      ? "green"
                      : gradeOf(g.document.id) === 1
                        ? "yellow"
                        : "gray"
                  }
                />
              </Group>
            </Card>
          ))}
          {results.data && results.data.groups.length === 0 && (
            <Text c="dimmed">{t("search.none")}</Text>
          )}
          {q && judgments.data && (
            <Text size="xs" c="dimmed">
              {t("admin.eval.graded", { count: judgments.data.items.length })}
            </Text>
          )}
        </Stack>
        <Paper withBorder p="sm" style={{ flex: 1, minWidth: 260 }}>
          <Text fw={600} size="sm" mb={4}>
            {t("admin.eval.topQueries")}
          </Text>
          <Table verticalSpacing={2} withRowBorders={false}>
            <Table.Tbody>
              {(log.data?.topQueries30d ?? []).slice(0, 20).map((row) => (
                <Table.Tr key={row.queryNorm}>
                  <Table.Td>
                    <Anchor
                      size="sm"
                      onClick={() => {
                        setText(row.queryNorm);
                        setQ(row.queryNorm);
                      }}
                    >
                      {row.queryNorm}
                    </Anchor>
                  </Table.Td>
                  <Table.Td ta="right" style={{ whiteSpace: "nowrap" }}>
                    <Badge size="xs" variant="light" style={{ overflow: "visible" }}>
                      {row.count}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right" c={row.avgDocs === 0 ? "red" : "dimmed"}>
                    {row.avgDocs}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      </Group>
    </Stack>
  );
}
